(function (global) {
  'use strict';

  var CONTRACT_VERSION = '1.1.0';
  var MAX_RESPONSE_BYTES = 262144;
  var MAX_CLOCK_SKEW_MS = 300000;
  var MAX_TOPICS = 6;
  var MAX_UNANSWERED_TOPICS = 3;
  var MIN_CONFIDENCE = 0.8;
  var REQUIRED_CONSENT_VERSION = 'community-public-v1';
  var REQUIRED_PRIVACY_CLASS = 'public_curated';
  var REQUIRED_OWNER = 'community-knowledge-data';
  var REQUIRED_MAPPING_SOURCE = 'PIM';
  var REQUIRED_MAPPING_STATUS = 'VALIDATED';
  var REQUIRED_MAPPING_VERSION = 'pim-community-product-v1';
  var MATCH_TYPES = ['exact_product', 'model_family'];
  var EVENT_NAMES = [
    'community_questions_tab_opened',
    'community_topic_opened',
    'community_question_started'
  ];
  var MATCH_PRIORITY = {
    exact_product: 0,
    model_family: 1
  };
  function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function hasSafeTopicUrl(value, communityOrigin) {
    try {
      var url = new URL(value);
      return url.protocol === 'https:' &&
        url.origin === communityOrigin &&
        url.username === '' &&
        url.password === '' &&
        url.search === '' &&
        url.hash === '' &&
        /^\/t\/[^/?#]+\/[1-9][0-9]*(?:\/[1-9][0-9]*)?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  function parseStrictUtcTimestamp(value) {
    if (typeof value !== 'string' || value.length < 20 || value.length > 24) return null;
    var match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z$/.exec(value);
    if (!match) return null;

    var parts = match.slice(1, 7).map(Number);
    var milliseconds = Number((match[7] || '').padEnd(3, '0')) || 0;
    var timestamp = Date.parse(value);
    if (!Number.isFinite(timestamp)) return null;

    var parsed = new Date(timestamp);
    if (parsed.getUTCFullYear() !== parts[0] ||
        parsed.getUTCMonth() + 1 !== parts[1] ||
        parsed.getUTCDate() !== parts[2] ||
        parsed.getUTCHours() !== parts[3] ||
        parsed.getUTCMinutes() !== parts[4] ||
        parsed.getUTCSeconds() !== parts[5] ||
        parsed.getUTCMilliseconds() !== milliseconds) {
      return null;
    }
    return timestamp;
  }

  function hasSafeSourceReference(value) {
    return typeof value === 'string' &&
      value === value.trim() &&
      value.length >= 3 &&
      value.length <= 500 &&
      !/[\u0000-\u001f\u007f<>"'`\s]/.test(value);
  }

  function hasValidGovernanceMetadata(topic, capturedAt, now) {
    var expiresAt = parseStrictUtcTimestamp(topic.expires_at);
    return topic.consent_version === REQUIRED_CONSENT_VERSION &&
      typeof topic.confidence === 'number' &&
      Number.isFinite(topic.confidence) &&
      topic.confidence >= MIN_CONFIDENCE &&
      topic.confidence <= 1 &&
      topic.privacy_class === REQUIRED_PRIVACY_CLASS &&
      topic.owner === REQUIRED_OWNER &&
      expiresAt !== null &&
      expiresAt > now &&
      expiresAt > capturedAt;
  }

  function hasSafeDisplayName(value) {
    return typeof value === 'string' &&
      value === value.trim() &&
      value.length >= 2 &&
      value.length <= 80 &&
      !/[\u0000-\u001f\u007f<>]/.test(value);
  }

  function hasSafeModelFamily(value) {
    return value === null ||
      (typeof value === 'string' &&
        value.length >= 5 &&
        value.length <= 100 &&
        /^fam-[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(value));
  }

  function normaliseContext(payload, context, nowMs) {
    var now = Number.isFinite(nowMs) ? nowMs : Date.now();
    if (!isPlainObject(payload) || payload.schema_version !== CONTRACT_VERSION) return null;
    if (!isPlainObject(payload.release) ||
        payload.release.read_ready !== true ||
        typeof payload.release.intake_ready !== 'boolean') {
      return null;
    }
    if (!isPlainObject(payload.product)) return null;
    if (payload.product.sku !== context.productSku) return null;
    if (payload.product.canonical_url !== context.productUrl) return null;
    if (payload.product.mapping_source !== REQUIRED_MAPPING_SOURCE ||
        payload.product.mapping_status !== REQUIRED_MAPPING_STATUS ||
        payload.product.mapping_version !== REQUIRED_MAPPING_VERSION ||
        !hasSafeModelFamily(payload.product.model_family)) {
      return null;
    }
    var mappingValidatedAt = parseStrictUtcTimestamp(payload.product.mapping_validated_at);
    if (mappingValidatedAt === null || mappingValidatedAt > now + MAX_CLOCK_SKEW_MS) return null;
    if (!Array.isArray(payload.topics)) return null;

    var topics = payload.topics
      .slice()
      .filter(function (topic) {
        if (!isPlainObject(topic)) return false;
        if (topic.review_status !== 'PUBLISHED') return false;
        if (!MATCH_TYPES.includes(topic.match_type)) return false;
        if (typeof topic.topic_id !== 'string' || !/^topic-[a-z0-9-]+$/.test(topic.topic_id)) return false;
        if (typeof topic.title !== 'string' || topic.title.trim().length < 8 || topic.title.trim().length > 180) return false;
        if (!hasSafeDisplayName(topic.author_display_name)) return false;
        if (typeof topic.specialist_answered !== 'boolean') return false;
        if (typeof topic.is_answered !== 'boolean') return false;
        if (!Number.isSafeInteger(topic.reply_count) || topic.reply_count < 0 || topic.reply_count > 100000) return false;
        if (!hasSafeSourceReference(topic.source_ref)) return false;
        var capturedAt = parseStrictUtcTimestamp(topic.source_captured_at);
        if (capturedAt === null || capturedAt > now + MAX_CLOCK_SKEW_MS) return false;
        var lastActivityAt = parseStrictUtcTimestamp(topic.last_activity_at);
        if (lastActivityAt === null || lastActivityAt > now + MAX_CLOCK_SKEW_MS) return false;
        if (!hasValidGovernanceMetadata(topic, capturedAt, now)) return false;
        if (!hasSafeTopicUrl(topic.canonical_topic_url, context.communityOrigin)) return false;
        if (topic.product_sku !== context.productSku) return false;
        if (topic.match_type === 'model_family') {
          if (payload.product.model_family === null || topic.model_family !== payload.product.model_family) return false;
        } else if (topic.model_family !== null) {
          return false;
        }
        if (topic.specialist_answered && !topic.is_answered) return false;
        if (topic.is_answered) {
          if (topic.reply_count < 1 || topic.response_due_at !== null) return false;
        } else {
          var responseDueAt = parseStrictUtcTimestamp(topic.response_due_at);
          if (topic.reply_count !== 0 ||
              topic.specialist_answered ||
              responseDueAt === null ||
              responseDueAt <= now) {
            return false;
          }
        }
        return true;
      })
      .sort(function (left, right) {
        if (left.is_answered !== right.is_answered) return left.is_answered ? -1 : 1;
        var priorityDifference = MATCH_PRIORITY[left.match_type] - MATCH_PRIORITY[right.match_type];
        if (priorityDifference !== 0) return priorityDifference;
        return Date.parse(right.last_activity_at) - Date.parse(left.last_activity_at);
      })
      .reduce(function (state, topic) {
        if (state.seenTopics.has(topic.topic_id) || state.seenUrls.has(topic.canonical_topic_url)) {
          return state;
        }
        if (!topic.is_answered && state.unansweredCount >= MAX_UNANSWERED_TOPICS) return state;
        if (state.selected.length < MAX_TOPICS) {
          state.selected.push(topic);
          state.seenTopics.add(topic.topic_id);
          state.seenUrls.add(topic.canonical_topic_url);
          if (!topic.is_answered) state.unansweredCount += 1;
        }
        return state;
      }, {
        selected: [],
        seenTopics: new Set(),
        seenUrls: new Set(),
        unansweredCount: 0
      }).selected;

    return {
      intakeReady: payload.release.intake_ready === true,
      product: payload.product,
      topics: topics
    };
  }

  function normaliseTopics(payload, context, nowMs) {
    var normalised = normaliseContext(payload, context, nowMs);
    return normalised ? normalised.topics : [];
  }

  function formatDate(value, locale) {
    try {
      return new Intl.DateTimeFormat(locale || 'nl', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(new Date(value));
    } catch (_) {
      return '';
    }
  }

  function getClientCopy(container) {
    if (!container || !container.dataset) return null;
    var copy = {
      locale: container.dataset.locale,
      sourceCapturedPrefix: container.dataset.copySourceCapturedPrefix,
      lastActivityPrefix: container.dataset.copyLastActivityPrefix,
      askedByPrefix: container.dataset.copyAskedByPrefix,
      specialistAnswered: container.dataset.copySpecialistAnswered,
      responseSingular: container.dataset.copyResponseSingular,
      responsePlural: container.dataset.copyResponsePlural,
      viewConversation: container.dataset.copyViewConversation,
      newTab: container.dataset.copyNewTab,
      empty: container.dataset.copyEmpty,
      unavailable: container.dataset.copyUnavailable,
      communityCta: container.dataset.copyCommunityCta,
      contactCta: container.dataset.copyContactCta,
      matchLabels: {
        exact_product: {
          heading: container.dataset.copyGroupExactHeading,
          badge: container.dataset.copyTypeExact
        },
        model_family: {
          heading: container.dataset.copyGroupModelFamilyHeading,
          badge: container.dataset.copyTypeModelFamily
        }
      }
    };
    var required = [
      copy.locale,
      copy.sourceCapturedPrefix,
      copy.lastActivityPrefix,
      copy.askedByPrefix,
      copy.specialistAnswered,
      copy.responseSingular,
      copy.responsePlural,
      copy.viewConversation,
      copy.newTab,
      copy.empty,
      copy.unavailable,
      copy.communityCta,
      copy.contactCta,
      copy.matchLabels.exact_product.heading,
      copy.matchLabels.exact_product.badge,
      copy.matchLabels.model_family.heading,
      copy.matchLabels.model_family.badge
    ];
    return required.every(function (value) {
      return typeof value === 'string' && value.trim() !== '';
    }) ? copy : null;
  }

  function createTopicCard(topic, container, copy) {
    var item = document.createElement('li');
    item.className = 'rounded-brand-lg border border-border bg-surface p-4 shadow-soft';

    var badge = document.createElement('span');
    badge.className = 'inline-flex rounded-full bg-brand-secondary/10 px-2.5 py-1 text-sm font-bold text-brand-ink';
    badge.textContent = copy.matchLabels[topic.match_type].badge;

    var title = document.createElement('h4');
    title.className = 'm-0 mt-3 font-display text-base font-bold leading-snug text-brand-ink';
    title.textContent = topic.title.trim();

    var responseLabel = topic.reply_count === 1 ? copy.responseSingular : copy.responsePlural;
    var activity = document.createElement('p');
    activity.className = 'm-0 mt-2 text-sm leading-relaxed text-text-muted';
    activity.textContent = copy.askedByPrefix + ' ' + topic.author_display_name +
      ' · ' + topic.reply_count + ' ' + responseLabel +
      ' · ' + copy.lastActivityPrefix + ' ' + formatDate(topic.last_activity_at, copy.locale) + '.';

    var specialist = null;
    if (topic.specialist_answered) {
      specialist = document.createElement('p');
      specialist.className = 'm-0 mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand-secondary/10 px-2.5 py-1 text-sm font-bold text-brand-ink';
      specialist.textContent = '✓ ' + copy.specialistAnswered;
    }

    var meta = document.createElement('p');
    meta.className = 'm-0 mt-2 text-sm leading-relaxed text-text-muted';
    meta.textContent = copy.sourceCapturedPrefix + ' ' + formatDate(topic.source_captured_at, copy.locale) + '.';

    var link = document.createElement('a');
    link.className = 'mt-3 inline-flex min-h-11 min-w-11 items-center gap-2 rounded-brand px-1 py-2 text-base font-bold text-brand-ink underline decoration-brand-secondary/30 underline-offset-4 transition-colors hover:text-brand-secondary focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-solid focus-visible:outline-focus motion-reduce:transition-none';
    link.href = topic.canonical_topic_url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.dataset.communityTopicLink = '';
    link.dataset.matchType = topic.match_type;
    link.dataset.topicId = topic.topic_id;
    link.textContent = copy.viewConversation;

    var srText = document.createElement('span');
    srText.className = 'sr-only';
    srText.textContent = ' ' + copy.newTab;
    link.appendChild(srText);

    item.appendChild(badge);
    item.appendChild(title);
    item.appendChild(activity);
    if (specialist) item.appendChild(specialist);
    item.appendChild(meta);
    item.appendChild(link);

    link.addEventListener('click', function () {
      track(container, 'community_topic_opened', {
        match_type: topic.match_type,
        topic_id: topic.topic_id
      });
    });

    return item;
  }

  function renderTopics(container, topics) {
    var groups = container.querySelector('[data-community-groups]');
    var empty = container.querySelector('[data-community-empty]');
    var copy = getClientCopy(container);
    if (!groups || !empty || !copy) return false;

    groups.textContent = '';
    MATCH_TYPES.forEach(function (matchType) {
      var matchingTopics = topics.filter(function (topic) {
        return topic.match_type === matchType;
      });
      if (matchingTopics.length === 0) return;

      var section = document.createElement('section');
      var headingId = 'community-' + matchType + '-' + container.dataset.productId + '-' + container.dataset.surface;
      section.setAttribute('aria-labelledby', headingId);

      var heading = document.createElement('h3');
      heading.id = headingId;
      heading.className = 'm-0 font-display text-lg font-bold text-brand-ink';
      heading.textContent = copy.matchLabels[matchType].heading;

      var list = document.createElement('ul');
      list.className = 'm-0 mt-3 grid list-none gap-3 p-0 lg:grid-cols-2';
      matchingTopics.forEach(function (topic) {
        list.appendChild(createTopicCard(topic, container, copy));
      });

      section.appendChild(heading);
      section.appendChild(list);
      groups.appendChild(section);
    });

    groups.classList.remove('hidden');
    empty.classList.add('hidden');
    return true;
  }

  function hasAnalyticsConsent(runtime) {
    var currentRuntime = runtime || global;
    try {
      if (currentRuntime.navigator && currentRuntime.navigator.globalPrivacyControl === true) return false;
    } catch (_) {}
    try {
      var customerPrivacy = currentRuntime.Shopify && currentRuntime.Shopify.customerPrivacy;
      if (customerPrivacy && typeof customerPrivacy.analyticsProcessingAllowed === 'function') {
        return customerPrivacy.analyticsProcessingAllowed() === true;
      }
      if (customerPrivacy && typeof customerPrivacy.currentVisitorConsent === 'function') {
        return customerPrivacy.currentVisitorConsent().analytics === 'yes';
      }
    } catch (_) {}
    return false;
  }

  function safeCatalogReference(value, pattern, maxLength) {
    if (typeof value !== 'string') return null;
    var trimmed = value.trim();
    if (!trimmed || trimmed.length > maxLength || !pattern.test(trimmed)) return null;
    return trimmed;
  }

  function buildTelemetryPayload(container, eventName, extra) {
    if (!container || !EVENT_NAMES.includes(eventName)) return null;
    var payload = {
      event_category: 'community_product_questions',
      contract_version: CONTRACT_VERSION
    };

    var productId = safeCatalogReference(container.dataset.productId, /^[1-9][0-9]*$/, 30);
    var productSku = safeCatalogReference(container.dataset.productSku, /^[a-zA-Z0-9._/+ -]+$/, 128);
    if (productId) payload.product_id = productId;
    if (productSku) payload.product_sku = productSku;

    if (extra && MATCH_TYPES.includes(extra.match_type)) {
      payload.match_type = extra.match_type;
    }
    if (extra && typeof extra.topic_id === 'string' && /^topic-[a-z0-9-]+$/.test(extra.topic_id)) {
      payload.topic_id = extra.topic_id;
    }
    return payload;
  }

  function track(container, eventName, extra, runtime) {
    var currentRuntime = runtime || global;
    if (!hasAnalyticsConsent(currentRuntime)) return false;
    var payload = buildTelemetryPayload(container, eventName, extra);
    if (!payload) return false;
    var tracked = false;

    if (typeof currentRuntime.gtag === 'function') {
      try {
        currentRuntime.gtag('event', eventName, payload);
        tracked = true;
      } catch (_) {}
    }
    if (currentRuntime.vcAmplitude && typeof currentRuntime.vcAmplitude.track === 'function') {
      try {
        currentRuntime.vcAmplitude.track(eventName, payload);
        tracked = true;
      } catch (_) {}
    }
    return tracked;
  }

  function setLoading(container, isLoading) {
    var loading = container.querySelector('[data-community-loading]');
    if (!loading) return;
    loading.classList.toggle('hidden', !isLoading);
    loading.classList.toggle('flex', isLoading);
    if (isLoading) {
      container.querySelector('[data-community-groups]')?.classList.add('hidden');
      container.querySelector('[data-community-empty]')?.classList.add('hidden');
    }
  }

  function setEmptyCopy(container, copy) {
    var empty = container.querySelector('[data-community-empty]');
    var emptyCopy = container.querySelector('[data-community-empty-copy]');
    container.querySelector('[data-community-groups]')?.classList.add('hidden');
    if (empty) empty.classList.remove('hidden');
    if (emptyCopy) emptyCopy.textContent = copy;
  }

  function hasSafeQuestionUrl(value, context) {
    try {
      var url = new URL(value);
      var keys = Array.from(url.searchParams.keys()).sort();
      var body = url.searchParams.get('body') || '';
      return url.protocol === 'https:' &&
        url.origin === context.communityOrigin &&
        url.username === '' &&
        url.password === '' &&
        url.pathname === '/new-topic' &&
        url.hash === '' &&
        keys.join(',') === 'body,tags,title' &&
        url.searchParams.get('title').length >= 8 &&
        url.searchParams.get('title').length <= 180 &&
        body.length <= 4000 &&
        body.includes(context.productSku) &&
        body.includes(context.productUrl) &&
        /^sku-[a-z0-9-]+$/.test(url.searchParams.get('tags') || '');
    } catch (_) {
      return false;
    }
  }

  function setQuestionRoute(container, communityReady, context) {
    var copy = getClientCopy(container);
    var link = container.querySelector('[data-community-question-link]');
    var label = container.querySelector('[data-community-question-label]');
    var publicNote = container.querySelector('[data-community-publication-note]');
    var fallbackNote = container.querySelector('[data-community-fallback-note]');
    var newTabText = container.querySelector('[data-community-new-tab-sr]');
    if (!copy || !link || !label) return false;

    var contactUrl = container.dataset.contactUrl || '';
    var validContactUrl = false;
    try {
      var contact = new URL(contactUrl);
      validContactUrl = contact.protocol === 'https:' &&
        contact.origin === 'https://vijvercentrum.nl' &&
        contact.pathname === '/pages/contact' &&
        contact.search === '' &&
        contact.hash === '' &&
        contact.username === '' &&
        contact.password === '';
    } catch (_) {}
    if (!validContactUrl) {
      link.removeAttribute('href');
      container.dataset.intakeState = 'blocked';
      return false;
    }

    var questionUrl = container.dataset.communityQuestionUrl || '';
    var canUseCommunity = communityReady === true &&
      context &&
      hasSafeQuestionUrl(questionUrl, context);

    link.href = canUseCommunity ? questionUrl : contactUrl;
    label.textContent = canUseCommunity ? copy.communityCta : copy.contactCta;
    link.toggleAttribute('target', canUseCommunity);
    if (canUseCommunity) {
      link.target = '_blank';
      link.rel = 'noopener';
    } else {
      link.removeAttribute('target');
      link.removeAttribute('rel');
    }
    publicNote?.classList.toggle('hidden', !canUseCommunity);
    fallbackNote?.classList.toggle('hidden', canUseCommunity);
    newTabText?.classList.toggle('hidden', !canUseCommunity);
    container.dataset.intakeState = canUseCommunity ? 'ready' : 'fallback';
    return canUseCommunity;
  }

  async function loadCommunityQuestions(container) {
    if (!container || container.dataset.loadState) return;
    container.dataset.loadState = 'loading';
    setLoading(container, true);

    var context = {
      productSku: container.dataset.productSku || '',
      productUrl: container.dataset.productUrl || '',
      communityOrigin: container.dataset.communityOrigin || ''
    };
    var copy = getClientCopy(container);

    if (!context.productSku || !context.productUrl || !context.communityOrigin || !copy) {
      container.dataset.loadState = 'empty';
      setLoading(container, false);
      return;
    }

    var controller = new AbortController();
    var timeout = global.setTimeout(function () {
      controller.abort();
    }, 4500);

    try {
      var endpoint = new URL(container.dataset.contextUrl);
      var endpointKeys = Array.from(endpoint.searchParams.keys()).sort();
      if (endpoint.protocol !== 'https:' ||
          endpoint.origin !== context.communityOrigin ||
          endpoint.username !== '' ||
          endpoint.password !== '' ||
          endpoint.pathname !== '/vc/product-context/v1.json' ||
          endpoint.hash !== '' ||
          endpointKeys.join(',') !== 'product_url,sku' ||
          endpoint.searchParams.get('sku') !== context.productSku ||
          endpoint.searchParams.get('product_url') !== context.productUrl) {
        throw new Error('invalid_context_endpoint');
      }
      var response = await fetch(endpoint.toString(), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        headers: {
          Accept: 'application/json'
        },
        signal: controller.signal
      });
      if (!response.ok || !(response.headers.get('content-type') || '').toLowerCase().includes('application/json')) {
        throw new Error('context_unavailable');
      }
      var contentLength = Number(response.headers.get('content-length') || 0);
      if (contentLength > MAX_RESPONSE_BYTES) throw new Error('context_too_large');
      var responseText = await response.text();
      if (responseText.length > MAX_RESPONSE_BYTES) throw new Error('context_too_large');
      var normalised = normaliseContext(JSON.parse(responseText), context);
      if (!normalised) throw new Error('invalid_context_payload');
      setQuestionRoute(container, normalised.intakeReady, context);
      var topics = normalised.topics;
      if (topics.length > 0 && renderTopics(container, topics)) {
        container.dataset.loadState = 'loaded';
      } else {
        setEmptyCopy(container, copy.empty);
        container.dataset.loadState = 'empty';
      }
    } catch (_) {
      setEmptyCopy(container, copy.unavailable);
      setQuestionRoute(container, false, context);
      container.dataset.loadState = 'unavailable';
    } finally {
      global.clearTimeout(timeout);
      setLoading(container, false);
    }
  }

  function initialiseContainer(container) {
    setQuestionRoute(container, false, {
      productSku: container.dataset.productSku || '',
      productUrl: container.dataset.productUrl || '',
      communityOrigin: container.dataset.communityOrigin || ''
    });
    container.querySelector('[data-community-question-link]')?.addEventListener('click', function () {
      if (container.dataset.intakeState === 'ready') {
        track(container, 'community_question_started');
      }
    });
  }

  function initialise() {
    document.querySelectorAll('[data-community-questions]').forEach(initialiseContainer);

    document.querySelectorAll('[data-product-tab="questions"]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var section = tab.closest('[data-product-tabs]');
        var panel = section?.querySelector('[data-product-panel="questions"]');
        var container = panel?.querySelector('[data-community-questions]');
        if (container) {
          loadCommunityQuestions(container);
          track(container, 'community_questions_tab_opened');
        }
      });
    });

    document.querySelectorAll('[data-accordion-community]').forEach(function (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        var container = details.querySelector('[data-community-questions]');
        if (container) {
          loadCommunityQuestions(container);
          track(container, 'community_questions_tab_opened');
        }
      });
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      CONTRACT_VERSION: CONTRACT_VERSION,
      MATCH_TYPES: MATCH_TYPES,
      EVENT_NAMES: EVENT_NAMES,
      REQUIRED_CONSENT_VERSION: REQUIRED_CONSENT_VERSION,
      REQUIRED_PRIVACY_CLASS: REQUIRED_PRIVACY_CLASS,
      REQUIRED_OWNER: REQUIRED_OWNER,
      REQUIRED_MAPPING_SOURCE: REQUIRED_MAPPING_SOURCE,
      REQUIRED_MAPPING_STATUS: REQUIRED_MAPPING_STATUS,
      REQUIRED_MAPPING_VERSION: REQUIRED_MAPPING_VERSION,
      MIN_CONFIDENCE: MIN_CONFIDENCE,
      normaliseContext: normaliseContext,
      normaliseTopics: normaliseTopics,
      parseStrictUtcTimestamp: parseStrictUtcTimestamp,
      hasSafeTopicUrl: hasSafeTopicUrl,
      hasSafeQuestionUrl: hasSafeQuestionUrl,
      hasAnalyticsConsent: hasAnalyticsConsent,
      buildTelemetryPayload: buildTelemetryPayload,
      track: track
    };
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialise, { once: true });
    } else {
      initialise();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
